import * as React from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Check, Globe, Loader2,
  Search, ShoppingCart, Trash2, Plus
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { usePlan } from "@/hooks/usePlans";
import { useDomainSearch } from "@/hooks/useDomainSearch";
import { useAddToCart } from "@/hooks/useCart";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { api, ApiError } from "@/lib/api";
import { PENDING_PURCHASE_KEY } from "@/pages/ResumePurchasePage";
import type { AddCartItemRequest, BillingCycle, CartDomainInput, DomainSearchResultDto, PlanDto } from "@/types/api";

type DomainMode = "register" | "transfer" | "existing";

interface SelectedDomain {
  domain: string;
  mode: DomainMode;
  price: number;      // year-1 reg fee; 0 for transfer/existing
  eppCode?: string;
  nameservers?: string[];
}

interface Registrant {
  name: string; organisation: string; email: string; phone: string;
  address: string; city: string; state: string; postcode: string; country: string;
}

const EMPTY_REGISTRANT: Registrant = {
  name: "", organisation: "", email: "", phone: "",
  address: "", city: "", state: "", postcode: "", country: ""
};

/**
 * Multi-domain purchase flow. A plan can carry up to plan.domain_capacity
 * domains; each is registered (paid), transferred (capture EPP), or connected
 * via nameservers (capture NS). Steps: term → domains → registrant → review,
 * then the whole bundle is added to the cart in one request.
 */
export function PurchaseFlowPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: plan, isLoading } = usePlan(slug);
  const addToCart = useAddToCart();
  const { status } = useAuth();

  const initialCycle = (params.get("cycle") as BillingCycle) || "monthly";
  const [step, setStep] = React.useState(1);
  const [cycle, setCycle] = React.useState<BillingCycle>(initialCycle);
  const [domains, setDomains] = React.useState<SelectedDomain[]>([]);
  const [registrant, setRegistrant] = React.useState<Registrant>(EMPTY_REGISTRANT);

  if (isLoading) return <FlowSkeleton />;
  if (!plan) {
    return (
      <Shell>
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
          That plan couldn't be found. <Link to="/plans" className="text-primary hover:underline">Back to plans</Link>.
        </CardContent></Card>
      </Shell>
    );
  }

  const capacity = plan.domain_capacity < 0 ? 99 : Math.max(plan.domain_capacity, 1);
  const needsRegistrant = domains.some(d => d.mode !== "existing");

  function addDomain(d: SelectedDomain) {
    if (domains.length >= capacity) {
      toast({ title: "Domain limit reached", description: `This plan allows up to ${capacity}.`, variant: "destructive" });
      return;
    }
    if (domains.some(x => x.domain === d.domain)) {
      toast({ title: "Already added", description: `${d.domain} is already in this order.`, variant: "destructive" });
      return;
    }
    setDomains(prev => [...prev, d]);
  }
  const removeDomain = (name: string) => setDomains(prev => prev.filter(d => d.domain !== name));

  async function submit(goCheckout: boolean) {
    if (addToCart.isPending || domains.length === 0) return;
    const reg = {
      name: registrant.name, organisation: registrant.organisation, email: registrant.email,
      phone: registrant.phone, address: registrant.address, city: registrant.city,
      state: registrant.state, postcode: registrant.postcode, country: registrant.country
    };
    const payloadDomains: CartDomainInput[] = domains.map(d => ({
      domain_name: d.domain,
      mode: d.mode === "existing" ? "UseExisting" : d.mode === "transfer" ? "Transfer" : "Register",
      registration_price: d.price,
      registration_years: 1,
      registrant: d.mode !== "existing" ? reg : undefined,
      epp_code: d.mode === "transfer" ? d.eppCode : undefined,
      nameservers: d.mode === "existing" ? d.nameservers : undefined
    }));

    const body: AddCartItemRequest = {
      item_type: "Plan", slug: plan!.slug, billing_cycle: cycle, domains: payloadDomains
    };

    // Login is enforced only here, at the very end. An anonymous user keeps
    // their full selection: we stash it and send them to log in, then the
    // resume page replays this exact add-to-cart and continues to checkout.
    if (status !== "authenticated") {
      localStorage.setItem(PENDING_PURCHASE_KEY, JSON.stringify({ body, goCheckout, savedAt: Date.now() }));
      navigate("/login", { state: { from: "/buy/resume" } });
      return;
    }

    try {
      await addToCart.mutateAsync(body);
      toast({ title: "Added to cart", description: `${plan!.name} + ${domains.length} domain(s)` });
      navigate(goCheckout ? "/checkout" : "/cart");
    } catch (err) {
      toast({
        title: "Couldn't add to cart",
        description: err instanceof ApiError ? err.message : "Please try again.",
        variant: "destructive"
      });
    }
  }

  return (
    <Shell>
      <button
        onClick={() => (step === 1 ? navigate("/plans") : setStep(s => s - 1))}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-primary"
      >
        <ArrowLeft className="h-4 w-4" />
        {step === 1 ? "Back to plans" : "Back"}
      </button>

      <Stepper step={step} />

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0 space-y-8">
          {step === 1 && (
            <>
              {/* Frequency cards at the top, domain attachment right below —
                  one screen, no extra trip. */}
              <BillingStep plan={plan} cycle={cycle} onCycle={setCycle} />
              <DomainsStep
                capacity={capacity}
                domains={domains}
                onAdd={addDomain}
                onRemove={removeDomain}
                onNext={() => setStep(needsRegistrant ? 2 : 3)}
              />
            </>
          )}
          {step === 2 && (
            <DetailsStep
              registrant={registrant} onChange={setRegistrant} onNext={() => setStep(3)}
            />
          )}
          {step === 3 && (
            <ReviewStep
              plan={plan} cycle={cycle} domains={domains}
              busy={addToCart.isPending}
              onAdd={() => submit(false)} onCheckout={() => submit(true)}
            />
          )}
        </div>

        <OrderSummary plan={plan} cycle={cycle} domains={domains} capacity={capacity} />
      </div>
    </Shell>
  );
}

// ── Billing-term selector (top of the plan & domain step) ────
function BillingStep({ plan, cycle, onCycle }: {
  plan: PlanDto; cycle: BillingCycle; onCycle: (c: BillingCycle) => void;
}) {
  const monthly = plan.price_monthly || 0;
  const terms: { id: BillingCycle; label: string; total: number | null; months: number }[] = [
    { id: "monthly",   label: "Monthly",   total: plan.price_monthly,   months: 1 },
    { id: "quarterly", label: "Quarterly", total: plan.price_quarterly, months: 3 },
    { id: "annually",  label: "Annually",  total: plan.price_annually,  months: 12 }
  ];
  return (
    <section className="space-y-4">
      <StepHeading title="Choose your billing term" sub={`${plan.name} — ${plan.plan_type} plan`} />
      <div className="grid gap-3 sm:grid-cols-3">
        {terms.filter(t => t.total != null).map(t => {
          const active = cycle === t.id;
          const total = t.total!;
          const list = monthly * t.months;                 // undiscounted "was" price
          const save = list - total;                       // absolute saving vs monthly
          const monthsFree = monthly > 0 ? Math.round(save / monthly) : 0; // $670 vs $804 -> 2 months free
          return (
            <button key={t.id} onClick={() => onCycle(t.id)}
              className={`relative flex flex-col items-center rounded-xl border px-3 py-3.5 text-center transition ${
                active
                  ? "border-primary bg-brand-accent/5 ring-1 ring-brand-accentLighter shadow-brand-sm"
                  : "border-border hover:border-primary/50"
              }`}>
              {active && (
                <span className="absolute right-2.5 top-2.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <Check className="h-2.5 w-2.5" />
                </span>
              )}
              <span className="text-xs font-semibold text-muted-foreground">{t.label}</span>
              <span className="mt-1.5 flex items-baseline gap-1">
                <span className="font-display text-2xl font-bold tracking-tight">{money(total)}</span>
                {save > 0 && <span className="text-[11px] text-muted-foreground line-through">{money(list)}</span>}
              </span>
              <span className="mt-0.5 text-[11px] text-muted-foreground">{money(total / t.months)} / mo</span>
              {save > 0 && (
                <span className="mt-2 inline-block rounded-full bg-brand-green/10 px-2 py-0.5 text-[10px] font-semibold text-brand-green">
                  {monthsFree > 0 ? `${monthsFree} month${monthsFree === 1 ? "" : "s"} free` : `Save ${money(save)}`}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}

// ── Domain attachment ────────────────────────────────────────
function DomainsStep({ capacity, domains, onAdd, onRemove, onNext }: {
  capacity: number;
  domains: SelectedDomain[];
  onAdd: (d: SelectedDomain) => void;
  onRemove: (name: string) => void;
  onNext: () => void;
}) {
  const full = domains.length >= capacity;
  return (
    <section className="space-y-4">
      <StepHeading
        title="Add your domains"
        sub={`This plan covers up to ${capacity} domain${capacity === 1 ? "" : "s"}. You've added ${domains.length}.`}
      />

      {domains.length > 0 && (
        <div className="space-y-2">
          {domains.map(d => (
            <div key={d.domain} className="flex items-center justify-between gap-3 rounded-lg border border-border px-4 py-2.5">
              <span className="inline-flex items-center gap-2 text-sm">
                <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-medium">{d.domain}</span>
                <Badge variant="outline" className="capitalize">{d.mode === "existing" ? "nameservers" : d.mode}</Badge>
              </span>
              <span className="flex items-center gap-3">
                <span className="text-sm">{d.price > 0 ? `${money(d.price)}/yr` : "Free"}</span>
                <button onClick={() => onRemove(d.domain)} className="text-muted-foreground hover:text-destructive" aria-label="Remove">
                  <Trash2 className="h-4 w-4" />
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      {full ? (
        <p className="rounded-lg border border-dashed border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          You've reached this plan's domain limit ({capacity}).
        </p>
      ) : (
        <AddDomainPanel onAdd={onAdd} />
      )}

      <div className="flex justify-end">
        <Button size="lg" disabled={domains.length === 0} onClick={onNext}>
          Continue <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </section>
  );
}

function AddDomainPanel({ onAdd }: { onAdd: (d: SelectedDomain) => void }) {
  const [mode, setMode] = React.useState<DomainMode>("register");
  const search = useDomainSearch();
  const { toast } = useToast();
  const [term, setTerm] = React.useState("");
  const [results, setResults] = React.useState<DomainSearchResultDto[] | null>(null);
  const [manual, setManual] = React.useState("");
  const [epp, setEpp] = React.useState("");
  const [ns, setNs] = React.useState("");
  const [transferPricing, setTransferPricing] = React.useState(false);
  const [transferError, setTransferError] = React.useState<string | null>(null);

  async function runSearch(e: React.FormEvent) {
    e.preventDefault();
    if (term.trim().length < 2 || search.isPending) return;
    try {
      const res = await search.mutateAsync(term);
      setResults(res.results);
    } catch (err) {
      toast({ title: "Search failed", description: err instanceof ApiError ? err.message : "Couldn't reach the domain service.", variant: "destructive" });
    }
  }

  const options: { id: DomainMode; label: string }[] = [
    { id: "register", label: "Register a new domain" },
    { id: "transfer", label: "Transfer a domain in" },
    { id: "existing", label: "Use an existing domain (change nameservers)" }
  ];

  return (
    <div className="space-y-3 rounded-xl border border-border p-4">
      <p className="inline-flex items-center gap-1.5 text-sm font-semibold"><Plus className="h-4 w-4" /> Add a domain</p>

      <div className="space-y-2">
        {options.map(o => (
          <button key={o.id} onClick={() => { setMode(o.id); setResults(null); }}
            className={`flex w-full items-center gap-3 rounded-lg border px-4 py-2.5 text-left text-sm transition ${
              mode === o.id ? "border-primary bg-brand-accent/5" : "border-border hover:border-primary/40"
            }`}>
            <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${mode === o.id ? "border-primary" : "border-muted-foreground/40"}`}>
              {mode === o.id && <span className="h-2 w-2 rounded-full bg-primary" />}
            </span>
            {o.label}
          </button>
        ))}
      </div>

      {mode === "register" && (
        <>
          <form onSubmit={runSearch} className="flex gap-2">
            <Input placeholder="Search a domain name..." value={term} onChange={e => setTerm(e.target.value)} />
            <Button type="submit" disabled={search.isPending} className="shrink-0">
              {search.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Search
            </Button>
          </form>
          {results && (
            <div className="space-y-2">
              {results.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">No results — try another name.</p>}
              {results.map(r => {
                const priceMissing = r.available && !r.price_available;
                return (
                <div key={r.domain} className="flex items-center justify-between gap-3 rounded-lg border border-border px-4 py-2.5">
                  <span className="truncate text-sm font-medium">
                    {r.domain} {r.premium && <Badge variant="outline" className="ml-1">Premium</Badge>}
                    <span className={`ml-2 text-xs ${r.available ? "text-brand-green" : "text-muted-foreground"}`}>
                      {r.available ? "Available" : "Taken"}
                    </span>
                  </span>
                  <span className="flex items-center gap-3">
                    {priceMissing ? (
                      <span className="text-xs text-amber-600">Price unavailable — please search again</span>
                    ) : (
                      r.price > 0 && <span className="text-sm">{money(r.price)}/yr</span>
                    )}
                    <Button size="sm" disabled={!r.available || priceMissing}
                      onClick={() => onAdd({ domain: r.domain, mode: "register", price: r.price })}>
                      Add
                    </Button>
                  </span>
                </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {mode === "transfer" && (
        <div className="space-y-2">
          <Label>Domain to transfer</Label>
          <Input placeholder="example.com" value={manual} onChange={e => setManual(e.target.value)} />
          <Label>EPP / Auth code</Label>
          <Input placeholder="From your current registrar" value={epp} onChange={e => setEpp(e.target.value)} />
          <p className="text-xs text-muted-foreground">
            Transferring extends the domain by 1 year — the live transfer price is fetched when you add it.
          </p>
          {transferError && <p className="text-xs text-destructive">{transferError}</p>}
          <Button disabled={!/\./.test(manual) || epp.trim().length === 0 || transferPricing}
            onClick={async () => {
              const name = manual.trim().toLowerCase();
              setTransferError(null);
              setTransferPricing(true);
              try {
                // Live re-price from the registrar (same price the cart/Stripe
                // will charge) — a transfer adds 1 year to the domain.
                const res = await api.get<{ success: boolean; result: DomainSearchResultDto }>(
                  `/api/domains/check?domain=${encodeURIComponent(name)}`
                );
                const price = res.result?.price ?? 0;
                if (!res.success || !res.result?.price_available || price <= 0) {
                  setTransferError("Couldn't fetch a live transfer price for this domain. Please try again.");
                  return;
                }
                onAdd({ domain: name, mode: "transfer", price, eppCode: epp.trim() });
                setManual(""); setEpp("");
              } catch {
                setTransferError("Couldn't fetch a live transfer price for this domain. Please try again.");
              } finally {
                setTransferPricing(false);
              }
            }}>
            {transferPricing ? "Fetching price…" : "Add transfer"}
          </Button>
        </div>
      )}

      {mode === "existing" && (
        <div className="space-y-2">
          <Label>Your domain</Label>
          <Input placeholder="example.com" value={manual} onChange={e => setManual(e.target.value)} />
          <Label>Current nameservers (optional, comma-separated)</Label>
          <Input placeholder="ns1.example.com, ns2.example.com" value={ns} onChange={e => setNs(e.target.value)} />
          <p className="text-xs text-muted-foreground">After checkout we'll give you our nameservers to point this domain at us.</p>
          <Button disabled={!/\./.test(manual)}
            onClick={() => {
              const list = ns.split(",").map(s => s.trim()).filter(Boolean);
              onAdd({ domain: manual.trim().toLowerCase(), mode: "existing", price: 0, nameservers: list.length ? list : undefined });
              setManual(""); setNs("");
            }}>
            Add domain
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Step 3: registrant (shared) ──────────────────────────────
function DetailsStep({ registrant, onChange, onNext }: {
  registrant: Registrant; onChange: (r: Registrant) => void; onNext: () => void;
}) {
  function field<K extends keyof Registrant>(k: K) {
    return { value: registrant[k], onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...registrant, [k]: e.target.value }) };
  }
  const required: (keyof Registrant)[] = ["name", "email", "phone", "address", "city", "postcode", "country"];
  const complete = required.every(k => registrant[k].trim().length > 0);

  return (
    <section className="space-y-4">
      <StepHeading title="Registrant details" sub="Used for the domains you're registering or transferring." />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Full name *" {...field("name")} />
        <Field label="Organisation" {...field("organisation")} />
        <Field label="Email *" type="email" {...field("email")} />
        <Field label="Phone *" {...field("phone")} placeholder="+1..." />
        <Field label="Address *" className="sm:col-span-2" {...field("address")} />
        <Field label="City *" {...field("city")} />
        <Field label="State / region" {...field("state")} />
        <Field label="Postcode *" {...field("postcode")} />
        <Field label="Country (ISO code) *" {...field("country")} placeholder="US, PK, GB..." />
      </div>
      <div className="flex justify-end">
        <Button size="lg" disabled={!complete} onClick={onNext}>Continue <ArrowRight className="h-4 w-4" /></Button>
      </div>
    </section>
  );
}

// ── Step 4: review ───────────────────────────────────────────
function ReviewStep({ plan, cycle, domains, busy, onAdd, onCheckout }: {
  plan: PlanDto; cycle: BillingCycle; domains: SelectedDomain[];
  busy: boolean; onAdd: () => void; onCheckout: () => void;
}) {
  return (
    <section className="space-y-4">
      <StepHeading title="Review & add to cart" sub="Add this to your cart, then check out securely with Stripe." />
      <Card><CardContent className="space-y-3 py-6">
        <Row label={`${plan.name} (${cycle})`} value={money(priceFor(plan, cycle))} />
        {domains.map(d => (
          <Row key={d.domain}
            label={<span className="inline-flex items-center gap-1.5"><Globe className="h-3.5 w-3.5" />{d.domain} <span className="text-xs text-muted-foreground capitalize">({d.mode === "existing" ? "nameservers" : d.mode})</span></span>}
            value={d.price > 0 ? `${money(d.price)}/yr` : "Free"} />
        ))}
        <div className="flex flex-col gap-2 pt-2 sm:flex-row">
          <Button size="lg" variant="outline" className="flex-1" disabled={busy} onClick={onAdd}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />} Add & keep browsing
          </Button>
          <Button size="lg" className="flex-1" disabled={busy} onClick={onCheckout}>
            Add & checkout <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </CardContent></Card>
    </section>
  );
}

// ── Order summary ────────────────────────────────────────────
function OrderSummary({ plan, cycle, domains, capacity }: {
  plan: PlanDto; cycle: BillingCycle; domains: SelectedDomain[]; capacity: number;
}) {
  const planPrice = priceFor(plan, cycle);
  const domainsTotal = domains.reduce((s, d) => s + (d.price ?? 0), 0);
  const total = planPrice + domainsTotal;
  return (
    <aside className="lg:sticky lg:top-6 h-fit">
      <Card><CardContent className="space-y-4 py-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-brand-accentDark">Order summary</p>
        <Row label={`${plan.name} (${cycle})`} value={money(planPrice)} />
        <Row label={`Domains (${domains.length}/${capacity})`} value={domainsTotal > 0 ? money(domainsTotal) : "Free"} />
        <div className="border-t border-border pt-3">
          <Row label={<span className="font-semibold text-foreground">Due today</span>}
               value={<span className="font-display text-lg font-bold">{money(total)}</span>} />
        </div>
        <p className="text-xs text-muted-foreground">Plan renews {cycle}; registered domains renew annually.</p>
      </CardContent></Card>
    </aside>
  );
}

// ── Bits ─────────────────────────────────────────────────────
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-[100dvh] bg-background">
      <div className="mx-auto w-full max-w-[1180px] space-y-6 px-4 py-8 sm:px-6 sm:py-10">
        {children}
      </div>
    </div>
  );
}
function Stepper({ step }: { step: number }) {
  const labels = ["Plan & Domain", "Details", "Review"];
  return (
    <div className="flex items-center gap-2 text-sm">
      {labels.map((l, i) => {
        const n = i + 1;
        const state = n < step ? "done" : n === step ? "active" : "todo";
        return (
          <React.Fragment key={l}>
            <span className={`inline-flex items-center gap-1.5 ${state === "todo" ? "text-muted-foreground" : "text-foreground"}`}>
              <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                state === "active" ? "bg-primary text-primary-foreground" : state === "done" ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
              }`}>
                {state === "done" ? <Check className="h-3.5 w-3.5" /> : n}
              </span>
              <span className="hidden sm:inline">{l}</span>
            </span>
            {n < labels.length && <span className="h-px w-4 bg-border sm:w-8" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}
function StepHeading({ title, sub }: { title: string; sub?: string }) {
  return (
    <div>
      <h1 className="font-display text-xl font-bold tracking-tight sm:text-2xl">{title}</h1>
      {sub && <p className="mt-1 text-sm text-muted-foreground">{sub}</p>}
    </div>
  );
}
function Field({ label, className, ...props }: { label: string; className?: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  const id = React.useId();
  return (
    <div className={`space-y-1.5 ${className ?? ""}`}>
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} {...props} />
    </div>
  );
}
function Row({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="min-w-0 text-muted-foreground">{label}</span>
      <span className="shrink-0 text-foreground">{value}</span>
    </div>
  );
}
function FlowSkeleton() {
  return (
    <Shell>
      <Skeleton className="h-5 w-28" />
      <Skeleton className="h-8 w-full max-w-md" />
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    </Shell>
  );
}
function money(n: number) { return `$${n.toFixed(2)}`; }
function priceFor(plan: PlanDto, cycle: BillingCycle): number {
  if (cycle === "annually")  return plan.price_annually  ?? plan.price_monthly * 12;
  if (cycle === "quarterly") return plan.price_quarterly ?? plan.price_monthly * 3;
  return plan.price_monthly;
}
